import type { NonEcommerceMarket } from '@/core/constants/non-ecommerce-markets';
import DEFAULT_FEATURE_FLAGS from '@/core/constants/feature-flags';
import parseLocale from '@/core/i18n/parse-locale';
import parseBoolean from '@/ui/utils/parseBoolean/parseBoolean';

/** ISO country codes gated behind {@link isExpansionMarketsOrderFlowEnabled} (UK, Switzerland). */
export const EXPANSION_MARKET_COUNTRY_IDS = ['GB', 'CH'] as const;

export type ExpansionMarketCountryId =
  (typeof EXPANSION_MARKET_COUNTRY_IDS)[number];

/**
 * Locales available for browse-only mode (experiences homepage + support, no shop)
 * when {@link isExpansionMarketsOrderFlowEnabled} is false.
 * Norway (NO) is a full EVA market and is not included here.
 */
export const EXPANSION_MARKET_BROWSE_COUNTRIES: NonEcommerceMarket[] = [
  {
    countryId: 'GB',
    countryName: 'United Kingdom',
    locales: [{ languageId: 'en', languageName: 'English', locale: 'en-gb' }],
  },
  {
    countryId: 'CH',
    countryName: 'Switzerland',
    locales: [
      { languageId: 'de', languageName: 'German', locale: 'de-ch' },
      { languageId: 'fr', languageName: 'French', locale: 'fr-ch' },
      { languageId: 'it', languageName: 'Italian', locale: 'it-ch' },
      { languageId: 'en', languageName: 'English', locale: 'en-ch' },
    ],
  },
];

export function isExpansionMarketCountryId(
  countryId: string,
): countryId is ExpansionMarketCountryId {
  return EXPANSION_MARKET_COUNTRY_IDS.includes(
    countryId.toUpperCase() as ExpansionMarketCountryId,
  );
}

export function isExpansionMarketLocale(locale: string): boolean {
  const { country } = parseLocale(locale);
  return isExpansionMarketCountryId(country);
}

/** Reads {@link DEFAULT_FEATURE_FLAGS.FEATURE_EXPANSION_MARKETS_ORDER_FLOW} (env-backed). Safe on server, edge, and client. */
export function isExpansionMarketsOrderFlowEnabled(): boolean {
  return parseBoolean(
    DEFAULT_FEATURE_FLAGS.FEATURE_EXPANSION_MARKETS_ORDER_FLOW,
  );
}

export function isExpansionMarketOrderFlowEnabledForCountry(  countryId: string,
): boolean {
  if (!isExpansionMarketCountryId(countryId)) {
    return true;
  }
  return isExpansionMarketsOrderFlowEnabled();
}

export function isExpansionMarketOrderFlowEnabledForLocale(
  locale: string,
): boolean {
  if (!isExpansionMarketLocale(locale)) {
    return true;
  }
  return isExpansionMarketsOrderFlowEnabled();
}

export function getExpansionBrowseLocalesByCountry(
  countryCode: string,
): NonEcommerceMarket['locales'] {
  const country = EXPANSION_MARKET_BROWSE_COUNTRIES.find(
    c => c.countryId === countryCode.toUpperCase(),
  );
  return country?.locales ?? [];
}

export function getExpansionBrowseCountryByLocale(
  locale: string,
): NonEcommerceMarket | undefined {
  return EXPANSION_MARKET_BROWSE_COUNTRIES.find(country =>
    country.locales.some(l => l.locale === locale.toLowerCase()),
  );
}

export function isExpansionMarketBrowseCountryId(countryId: string): boolean {
  if (!isExpansionMarketCountryId(countryId)) {
    return false;
  }
  return !isExpansionMarketsOrderFlowEnabled();
}

export function filterOrganizationsForExpansionMarkets<
  T extends { countryId: string },
>(organizations: T[]): T[] {
  if (isExpansionMarketsOrderFlowEnabled()) {
    return organizations;
  }
  return organizations.filter(
    org => !isExpansionMarketCountryId(org.countryId),
  );
}
